import {
  businessName,
  formatMoney,
  type Locale,
  organizationOf,
  type PublicBooking,
  pickLocale,
} from '@bb/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  publicBookingQuery,
  useCancelBooking,
  useRefundPreview,
  useRescheduleBooking,
} from '@/features/public-booking/api';
import { SlotPicker } from '@/features/public-booking/slot-picker';
import { ApiRequestError } from '@/lib/api';
import { ensureNamespace } from '@/lib/i18n';
import { btnSecondary as btn } from '@/lib/ui';

interface ManageSearch {
  new?: boolean;
  paid?: boolean;
}

/** Accepts `?paid=1`, `?paid=true` (Stripe success URL) and the router's own `?paid=true`. */
const flag = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true';

export const Route = createFileRoute('/booking/$token')({
  validateSearch: (s: Record<string, unknown>): ManageSearch => ({
    ...(flag(s.new) ? { new: true } : {}),
    ...(flag(s.paid) ? { paid: true } : {}),
  }),
  loader: async ({ context, params }) => {
    await ensureNamespace('booking');
    try {
      return await context.queryClient.ensureQueryData(publicBookingQuery(params.token));
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: ManageBookingPage,
});

/** The same shape as `when`, for a time that is being considered rather than booked. */
function whenAt(iso: string, zone: string, locale: string, durationMinutes: number): string {
  const s = DateTime.fromISO(iso, { zone }).setLocale(locale);
  const e = s.plus({ minutes: durationMinutes });
  return `${s.toFormat('DDDD')} · ${s.toLocaleString(DateTime.TIME_SIMPLE)} – ${e.toLocaleString(DateTime.TIME_SIMPLE)} (${s.offsetNameShort})`;
}

function when(b: PublicBooking, zone: string, locale: string): string {
  const s = DateTime.fromISO(b.start, { zone }).setLocale(locale);
  const e = DateTime.fromISO(b.end, { zone }).setLocale(locale);
  return `${s.toFormat('DDDD')} · ${s.toLocaleString(DateTime.TIME_SIMPLE)} – ${e.toLocaleString(DateTime.TIME_SIMPLE)} (${s.offsetNameShort})`;
}

function ManageBookingPage() {
  const { token } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['booking', 'common', 'errors']);
  const locale = i18n.language as Locale;
  const { data: booking } = useQuery({
    ...publicBookingQuery(token),
    initialData: Route.useLoaderData(),
  });
  const [mode, setMode] = useState<'view' | 'cancel' | 'reschedule'>('view');
  /*
    The time picked, before anything is done about it.

    Choosing used to move the booking the instant a slot was tapped, which on a
    grid of times is one mis-tap away from spending a change the booker may only
    have two of — with nothing to undo it.
  */
  const [pending, setPending] = useState<string | null>(null);

  /*
    Mid-task: the booker is picking a new time or confirming a cancellation.

    Everything on this page that is not that becomes noise — a Join meeting
    button, a receipt, an offer to add it to a calendar they are about to
    change, and the very buttons that started the task. The booking's name,
    time and place stay, because knowing which appointment is being moved is
    the one piece of context the task needs.
  */
  const busy = mode !== 'view';
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const cancel = useCancelBooking(token);
  const reschedule = useRescheduleBooking(token);
  const refund = useRefundPreview(token, mode === 'cancel' && booking.payment?.status === 'paid');

  // After Checkout: poll until the webhook confirms.
  useEffect(() => {
    if (!search.paid || booking.status !== 'awaiting_payment') return;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      const fresh = await queryClient.fetchQuery(publicBookingQuery(token));
      if (fresh.status !== 'awaiting_payment' || tries >= 30) clearInterval(id);
    }, 2000);
    return () => clearInterval(id);
  }, [search.paid, booking.status, queryClient, token]);

  /** Confirmed, not merely chosen: this is what spends one of the booker's changes. */
  const confirmMove = (iso: string) =>
    reschedule.mutate(iso, {
      onSuccess: ({ token: next, booking: updated }) => {
        queryClient.setQueryData(publicBookingQuery(token).queryKey, updated);
        setPending(null);
        setMode('view');
        setFlash(t('booking:public.status.rescheduled'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (next !== token) {
          void navigate({
            to: '/booking/$token',
            params: { token: next },
            search: {},
            replace: true,
          });
        }
      },
      onError: (err) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
    });

  const name = pickLocale(booking.eventType.name, locale, booking.host.locale);
  const zone = booking.booker.timezone;
  const otherZone = zone !== booking.hostTimezone;
  const message =
    booking.eventType.afterBooking.kind === 'confirmation_page'
      ? pickLocale(booking.eventType.afterBooking.message, locale, booking.host.locale)
      : '';

  useEffect(() => {
    if (
      search.new &&
      booking.eventType.afterBooking.kind === 'redirect' &&
      booking.eventType.afterBooking.redirectUrl
    ) {
      window.location.assign(booking.eventType.afterBooking.redirectUrl);
    }
  }, [search.new, booking.eventType.afterBooking]);

  /*
    Whether the appointment has happened, worked out from the times rather than
    from the status. Nothing ever sets `completed`: a booking stays `confirmed`
    long after it is over, so reading the status alone left this page offering
    to reschedule an appointment that finished an hour ago, and a Join meeting
    button for a call nobody is in.

    `end`, not `start`: during the half hour someone has booked, joining is
    exactly what they came here to do.
  */
  const past =
    (booking.status === 'confirmed' ||
      booking.status === 'completed' ||
      booking.status === 'no_show') &&
    new Date(booking.end).getTime() <= Date.now();

  const statusBanner = (() => {
    if (past) return t('booking:public.status.past');
    switch (booking.status) {
      case 'awaiting_payment':
        return search.paid
          ? t('booking:public.status.confirming')
          : t('booking:public.status.awaitingPayment');
      case 'confirmed':
        return search.new || search.paid
          ? t('booking:public.status.confirmedNew')
          : t('booking:public.status.confirmed');
      case 'cancelled':
        return t('booking:public.status.cancelled');
      case 'completed':
      case 'no_show':
        return t('booking:public.status.past');
      default:
        return null;
    }
  })();

  const gcal = new URL('https://calendar.google.com/calendar/render');
  gcal.searchParams.set('action', 'TEMPLATE');
  // Their own calendar entry, so it names the business rather than whichever
  // person of it they happen to be seeing.
  gcal.searchParams.set('text', `${name} – ${businessName(booking.host)}`);
  gcal.searchParams.set(
    'dates',
    `${booking.start.replace(/[-:]|\.\d{3}/g, '')}/${booking.end.replace(/[-:]|\.\d{3}/g, '')}`,
  );
  if (booking.joinUrl) gcal.searchParams.set('location', booking.joinUrl);

  return (
    <main className="mx-auto max-w-lg px-4 py-12">
      {flash ? (
        <p
          role="status"
          className="mb-6 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm font-medium text-green-800"
        >
          {flash}
        </p>
      ) : null}
      {statusBanner && !flash ? (
        <p
          className={`mb-6 rounded px-3 py-2 text-sm ${
            booking.status === 'cancelled' || past
              ? 'bg-neutral-100 text-neutral-700'
              : booking.status === 'awaiting_payment'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-green-50 text-green-800'
          }`}
        >
          {statusBanner}
        </p>
      ) : null}

      <p className="text-sm text-neutral-500">
        {organizationOf(booking.host)
          ? t('booking:public.hostFromOrg', {
              name: booking.host.name,
              org: organizationOf(booking.host),
            })
          : booking.host.name}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{name}</h1>
      <div className="mt-3 text-sm">
        <div>{when(booking, zone, locale)}</div>
        {otherZone ? (
          <div className="text-xs text-neutral-500">
            {when(booking, booking.hostTimezone, locale)}
          </div>
        ) : null}
        <div className="mt-1 text-neutral-600">
          {t(`booking:eventTypes.location.${booking.eventType.location.kind}`)}
        </div>
        {/* Still offered while the appointment is running, never after it. */}
        {booking.joinUrl && !past && !busy ? (
          <a
            href={booking.joinUrl}
            className="mt-2 flex min-h-11 w-full items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white sm:inline-flex sm:w-auto"
          >
            {t('booking:public.join')}
          </a>
        ) : null}
        {booking.payment && !busy ? (
          <div className="mt-2 text-neutral-600">
            {t(`booking:public.payment.${booking.payment.status}`, {
              amount: formatMoney(
                { amount: booking.payment.amount, currency: booking.payment.currency },
                locale,
              ),
            })}
            {booking.payment.refundedAmount > 0
              ? ` · ${t('booking:public.payment.refundedAmount', { amount: formatMoney({ amount: booking.payment.refundedAmount, currency: booking.payment.currency }, locale) })}`
              : ''}
            {booking.payment.receiptUrl ? (
              <>
                {' · '}
                <a href={booking.payment.receiptUrl} className="underline">
                  {t('booking:public.payment.receipt')}
                </a>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      {/*
        The host's own words to someone who has just booked — "we look forward
        to speaking with you". Already withheld once the booking is cancelled;
        an appointment that has been and gone needs the same silence.
      */}
      {message && booking.status === 'confirmed' && !past && !busy ? (
        <p className="mt-4 whitespace-pre-line rounded bg-neutral-50 p-3 text-sm">{message}</p>
      ) : null}

      {booking.status === 'awaiting_payment' && booking.checkoutUrl ? (
        <a
          href={booking.checkoutUrl}
          className="mt-6 flex min-h-12 w-full items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white sm:inline-flex sm:w-auto"
        >
          {t('booking:public.payNow')}
        </a>
      ) : null}

      {/*
        Nothing to add when the host's calendar has already invited them: the
        appointment is in their calendar, and both buttons here would put a
        second copy beside it. The Google Calendar one especially — it is a
        TEMPLATE link, which pre-fills a *new* event and carries no identity at
        all, so nothing can ever reconcile it with the invitation.

        The .ics stays available for anyone who declined the invitation or keeps
        a different calendar, but as a quiet link rather than the main action.
      */}
      {booking.status === 'confirmed' && !past && !busy && booking.calendarInvited ? (
        <p className="mt-6 text-sm text-neutral-600">
          {t('booking:public.alreadyInCalendar', { host: booking.host.name })}{' '}
          <a href={`/api/v1/public/bookings/${token}/ics`} className="underline">
            {t('booking:public.addAnyway')}
          </a>
        </p>
      ) : null}

      {booking.status === 'confirmed' && !past && !busy ? (
        <div className="mt-6 grid grid-cols-2 gap-2 text-sm sm:flex sm:flex-wrap">
          {!booking.calendarInvited ? (
            <>
              <a href={`/api/v1/public/bookings/${token}/ics`} className={btn}>
                {t('booking:public.addToCalendar')}
              </a>
              <a href={gcal.href} target="_blank" rel="noreferrer" className={btn}>
                Google Calendar
              </a>
            </>
          ) : null}
          {booking.eventType.exists && booking.eventType.rescheduleAllowed ? (
            <button type="button" onClick={() => setMode('reschedule')} className={btn}>
              {t('booking:public.reschedule')}
            </button>
          ) : null}
          {booking.cancelAllowed ? (
            <button
              type="button"
              onClick={() => setMode('cancel')}
              className={`${btn} text-red-700`}
            >
              {t('booking:public.cancel')}
            </button>
          ) : null}
        </div>
      ) : null}

      {/*
        Said rather than left as an absence. Someone who cannot make it needs to
        know what to do instead, and the host's address is on the confirmation
        they already have.
      */}
      {booking.status === 'confirmed' && !past && !busy && !booking.cancelAllowed ? (
        <p className="mt-3 text-xs text-neutral-500">
          {t('booking:public.cancelNotAllowed', { host: booking.host.name })}
        </p>
      ) : null}

      {booking.status === 'confirmed' &&
      !past &&
      !busy &&
      booking.eventType.exists &&
      !booking.eventType.rescheduleAllowed &&
      booking.eventType.rescheduleBlockedReason ? (
        <p className="mt-3 text-xs text-neutral-500">
          {t(`booking:public.rescheduleBlocked.${booking.eventType.rescheduleBlockedReason}`)}
        </p>
      ) : null}

      {/*
        The same offer for both endings: the appointment is not happening again
        by itself, whether it was called off or has simply been and gone.
      */}
      {(booking.status === 'cancelled' || past) && !busy && booking.host.username ? (
        <Link
          to="/$username/$slug"
          params={{ username: booking.host.username, slug: booking.eventType.slug }}
          className={`${btn} mt-6 inline-block`}
        >
          {t('booking:public.bookAgain')}
        </Link>
      ) : null}

      {mode === 'cancel' ? (
        <section className="mt-6 space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">{t('booking:public.cancelTitle')}</h2>
          {booking.payment?.status === 'paid' && refund.data ? (
            <p className="text-sm text-neutral-600">
              {refund.data.amount > 0
                ? t('booking:public.refundPreview', {
                    amount: formatMoney(
                      { amount: refund.data.amount, currency: refund.data.currency },
                      locale,
                    ),
                    percent: refund.data.percent,
                  })
                : /*
                    Nought has two causes and they are not the same sentence.
                    A service that never refunds has no window to be outside
                    of, and telling someone they missed a deadline that does
                    not exist invites them to ask when it was.
                  */
                  booking.eventType.refundPolicy.kind === 'none'
                  ? t('booking:public.noRefundPolicy')
                  : t('booking:public.noRefund')}
            </p>
          ) : null}
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('booking:public.reasonPlaceholder')}
            className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={cancel.isPending}
              onClick={() =>
                cancel.mutate(reason, {
                  onSuccess: (b) => {
                    queryClient.setQueryData(publicBookingQuery(token).queryKey, b);
                    setMode('view');
                  },
                  onError: (err) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
                })
              }
              className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
            >
              {t('booking:public.confirmCancel')}
            </button>
            <button type="button" onClick={() => setMode('view')} className={btn}>
              {t('common:actions.back')}
            </button>
          </div>
        </section>
      ) : null}

      {mode === 'reschedule' && booking.host.username ? (
        <section className="mt-6 space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">
            {pending ? t('booking:public.confirmMoveTitle') : t('booking:public.rescheduleTitle')}
          </h2>
          {pending ? (
            <div className="space-y-3">
              <p className="text-sm">
                <span className="text-neutral-500">{t('booking:public.confirmMoveTo')}: </span>
                <span className="font-medium">
                  {whenAt(pending, zone, locale, booking.eventType.durationMinutes)}
                </span>
              </p>
              {/*
                Said before it is spent, not after. The count comes from the
                booking, so it is the host's current limit rather than the one
                in force when the booking was made.
              */}
              {booking.eventType.reschedulesLeft > 0 ? (
                <p className="text-xs text-neutral-500">
                  {booking.eventType.reschedulesLeft === 1
                    ? t('booking:public.usesLastChange')
                    : t('booking:public.usesOneChange', {
                        count: booking.eventType.reschedulesLeft,
                      })}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={reschedule.isPending}
                  onClick={() => confirmMove(pending)}
                  className="min-h-11 rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                >
                  {t('booking:public.confirmMove')}
                </button>
                <button type="button" onClick={() => setPending(null)} className={btn}>
                  {t('booking:public.pickAnother')}
                </button>
              </div>
            </div>
          ) : null}
          {/*
            Hidden rather than unmounted: taking it out of the tree loses the
            day the picker has open, so "choose a different time" would drop the
            booker back to a bare calendar they had already navigated.
          */}
          <div className={pending ? 'hidden' : ''}>
            <SlotPicker
              username={booking.host.username}
              slug={booking.eventType.slug}
              locale={locale}
              timezone={zone}
              onTimezoneChange={() => undefined}
              maxDaysAhead={60}
              onSelect={(iso) => setPending(iso)}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setPending(null);
              setMode('view');
            }}
            className={btn}
          >
            {t('common:actions.back')}
          </button>
        </section>
      ) : null}

      {error ? (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {t(`errors:${error}`)}
        </p>
      ) : null}
    </main>
  );
}
